/** Opens Hub Events and starts the walkthrough. */
export const EVENTS_TOUR_HREF = '/hub?openTab=Events&eventTour=1';

export const EVENT_TOUR_PARAM = 'eventTour';

export type EventsTourStepId =
  | 'intro'
  | 'list'
  | 'createEvent'
  | 'createDetails'
  | 'createNotify'
  | 'createChecklist'
  | 'createAssets'
  | 'createSubmit'
  | 'settings'
  | 'settingsIntro'
  | 'addRule'
  | 'ruleName'
  | 'readsAs'
  | 'when'
  | 'checkFrequency'
  | 'event'
  | 'notify'
  | 'checklist'
  | 'assetChecklists'
  | 'preview'
  | 'actions';

export type EventsTourStepDef = {
  id: EventsTourStepId;
  title: string;
  description: string;
  /** `data-tour` value. Intro has no element (centered popover). */
  tour?: string;
};

export const EVENTS_TOUR_STEPS: EventsTourStepDef[] = [
  {
    id: 'intro',
    tour: 'events-intro',
    title: 'Events',
    description:
      'An event is a situation you are tracking — a storm, outage, or other threat to your assets. Create one by hand, or let an automation rule open one when live data matches.',
  },
  {
    id: 'list',
    tour: 'events-list',
    title: 'Open events',
    description:
      'Every Event has a card. Expand one to see the details, the Response Plan, and who has already been notified, so the whole response is in one place.',
  },
  {
    id: 'createEvent',
    tour: 'events-create',
    title: 'Create an event now',
    description:
      'Create Event records a situation the moment you know about it and puts it on this list for the whole org.',
  },
  {
    id: 'createDetails',
    tour: 'create-event-details',
    title: 'Event details',
    description:
      'These fields are how the org recognizes the situation at a glance. Name it, pick a category (a custom name appears when you select Other), and set Low, Medium, or High so people know the urgency. Responsible AOR searches units and labels who is taking the lead. Add a description, Started at, and an optional location by searching, dropping a point, or drawing an area so the situation shows on the map. The left rail opens one of Event details, Notify people, Response Plan, or Per-asset response plans at a time.',
  },
  {
    id: 'createNotify',
    tour: 'create-event-notify',
    title: 'Notify on create',
    description:
      'The people who need to act can hear about the situation the moment the event exists. Pick channels and recipients, set a priority, and ask them to confirm they received it.',
  },
  {
    id: 'createChecklist',
    tour: 'create-event-checklist',
    title: 'Response Plan',
    description:
      'Add Task builds the work the org will do for this situation. Those tasks become org tasks when you create the event, so they show up for the people responsible. Click an item to set status, point of contact, sub-tasks, or the channels and message for Send Notification.',
  },
  {
    id: 'createAssets',
    tour: 'create-event-assets',
    title: 'Impacted assets',
    description:
      'Name the resources this situation puts at risk and add a task for each one, so crews know which assets are involved and what to do for them.',
  },
  {
    id: 'createSubmit',
    tour: 'create-event-submit',
    title: 'Create when you are ready',
    description:
      'Create records the event, sends the notifications you configured, and attaches the Response Plan and per-asset tasks, so the org can act from one record.',
  },
  {
    id: 'settings',
    tour: 'events-settings',
    title: 'Automation Rules',
    description:
      'A rule can open the event for you when live data matches, so a storm cone or an outage on the map starts the response before anyone types it in.',
  },
  {
    id: 'settingsIntro',
    tour: 'event-settings-intro',
    title: 'Automation rules',
    description:
      'Each rule here is a watch on live data. Once you save it and turn it on, PRATUS opens an event when your conditions are met, with the plan and the notifications already attached.',
  },
  {
    id: 'addRule',
    tour: 'add-rule',
    title: 'Start a new rule',
    description:
      'Add Rule starts a draft you can finish before it watches anything.',
  },
  {
    id: 'ruleName',
    tour: 'rule-name',
    title: 'Name your rule',
    description:
      'A clear name is how people find this watch later in the list. Leave Enabled off while you set it up, then turn it on and Save when you want PRATUS to start watching.',
  },
  {
    id: 'readsAs',
    tour: 'reads-as',
    title: 'This rule reads as',
    description:
      'This sentence updates as you work, so you can read the whole rule in plain English and catch a wrong condition before the rule ever fires.',
  },
  {
    id: 'when',
    tour: 'when',
    title: 'When the rule should fire',
    description:
      'Point the rule at a live feed — a tropical cone, a map layer, or a custom URL — and say when it matters: matching attributes, or assets and AORs that overlap or sit near a feature. AND and OR let you require several conditions or accept any one of them, so the event opens only when the threat actually reaches you.',
  },
  {
    id: 'checkFrequency',
    tour: 'check-frequency',
    title: 'How often to check',
    description:
      'Choose how often PRATUS re-reads the feed, so the watch stays as fresh as the situation requires.',
  },
  {
    id: 'event',
    tour: 'event',
    title: 'What the event looks like',
    description:
      'The event people see is filled in from the live match, so the name, urgency, and who leads are already right when it opens. Set the category and Low, Medium, or High. The name is a template — tokens such as @ruleName fill in from the match. AOR can be None, Fixed AOR(s), From Impacted Resources, or From Impacted AOR(s), and it labels who is taking the lead. The description explains why it fired. With Prevent Duplicate Events on, a later check inside the hour window updates the existing event. Notify recipients that its data changed is on by default; turn it off and later checks still update the event, quietly.',
  },
  {
    id: 'notify',
    tour: 'notify',
    title: 'Who to notify',
    description:
      'When the rule fires, the right people hear about it on the channel they will actually see: in-app, email, text, or Teams, sent to the people and roles you choose. Set a priority and ask them to confirm they received it.',
  },
  {
    id: 'checklist',
    tour: 'checklist',
    title: 'Response Plan',
    description:
      'When the rule opens an event, this plan is already on it, so the org starts from a prepared list. Drag the grip to reorder items. ',
  },
  {
    id: 'assetChecklists',
    tour: 'asset-checklists',
    title: 'Per-asset response plans',
    description:
      'Choose a resource and click Add Asset Response Plan. That plan is stored on the asset for this rule, so the next time the rule touches that resource the tasks are already there. ',
  },
  {
    id: 'preview',
    tour: 'rule-preview',
    title: 'Preview the rule',
    description:
      'Preview runs this rule against current data so you can see whether today would open an event, before you turn the watch on. Fill in When first if you want that check to use a real feed.',
  },
  {
    id: 'actions',
    tour: 'rule-actions',
    title: 'Save your rule',
    description:
      'Save keeps the rule so you can come back to it. Delete removes it. Turn Enabled on, then Save, when you want PRATUS to start watching live data and open the event for you.',
  },
];

export function eventsTourStepIds(): EventsTourStepId[] {
  return EVENTS_TOUR_STEPS.map((step) => step.id);
}

export function eventsTourStepTitles(): string[] {
  return EVENTS_TOUR_STEPS.map((step) => step.title);
}
